/**
 * Wind Chill Calculator
 *
 * Calculates wind chill temperature based on current temperature and wind speed.
 * Uses Environment Canada's wind chill formula.
 *
 * Formula Sources:
 * - For wind speeds >= 5 km/h:
 *   W = 13.12 + 0.6215T - 11.37V^0.16 + 0.3965TV^0.16
 * - For wind speeds 0-5 km/h:
 *   W = T + ((-1.59 + 0.1345T)/5)V
 *
 * Where:
 * W = Wind chill index
 * T = Air temperature (°C)
 * V = Wind speed (km/h)
 *
 * Parameters:
 * @param {Object} temperatureElement - Temperature data with value and QC flag
 * @param {Object} windSpeedElement - Wind speed data with value and QC flag
 *
 * Returns:
 * @returns {number|null} - Wind chill temperature in °C or null if:
 *   - Temperature or wind speed is missing
 *   - QC flags indicate unreliable data
 *   - Temperature is above 0°C
 *   - Wind speed is 0 km/h
 *
 * Quality Control:
 * - Returns null if either temperature or wind speed has QC issues
 * - Only calculates wind chill when temperature is below 0°C
 * - Handles edge cases for very low wind speeds
 */

// COMPLEX: Wind chill calculation using Environment Canada's formula
// This function implements two different formulas based on wind speed
// Includes complex mathematical operations and edge case handling

// formula for calculating wind chill is taken from https://climate.weather.gc.ca/glossary_e.html#windChill

export const calculateWindChill = (temperatureElement, windSpeedElement) => {
  // Return "-" if values are missing
  if (!temperatureElement?.value || !windSpeedElement?.value) {
    return null;
  }

  // Check QC values only if they exist, and return "-" if they're not 100
  if (
    (temperatureElement.qc_value !== undefined &&
      temperatureElement.qc_value !== 100) ||
    (windSpeedElement.qc_value !== undefined &&
      windSpeedElement.qc_value !== 100)
  ) {
    return null;
  }

  const temperature = temperatureElement.value;
  const windSpeedKmH = windSpeedElement.value * 3.6; // Convert m/s to km/h

  // Return temperature if conditions aren't met for wind chill
  if (temperature > 0) {
    return null;
  }

  let windChill;

  // Formula 1: For wind speeds >= 5 km/h
  if (windSpeedKmH >= 5) {
    windChill =
      13.12 +
      0.6215 * temperature -
      11.37 * Math.pow(windSpeedKmH, 0.16) +
      0.3965 * temperature * Math.pow(windSpeedKmH, 0.16);
  }
  // Formula 2: For wind speeds between 0-5 km/h
  else if (windSpeedKmH > 0) {
    windChill =
      temperature + ((-1.59 + 0.1345 * temperature) / 5) * windSpeedKmH;
  }
  // No wind chill if no wind
  else {
    return null;
  }

  return Math.round(windChill * 10) / 10;
};
